import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtDT, fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ClipboardList, CheckCircle2, XCircle, Clock, Filter, ArrowLeft, ArrowRight, Search, UserPlus, User, AlertTriangle, Trash2, Shield } from "lucide-react";
import VoiceInput from "@/components/VoiceInput";
import KiviHealthBridge from "@/components/KiviHealthBridge";

const NEWPT_NUMERIC = { age: 3, mobile: 10, alt_mobile: 10, relative_mobile: 10, pincode: 6 };

const today = () => new Date().toISOString().slice(0, 10);

const emptyPt = { name: "", age: "", dob: "", gender: "Male", mobile: "", alt_mobile: "", address: "", city: "Jamnagar", state: "Gujarat", pincode: "", relative_name: "", relative_mobile: "", blood_group: "", reference_doctor: "", pmjay: false };

export default function OPD() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canDelete = ["admin", "super_admin"].includes(user?.role);
  const [sp] = useSearchParams();
  const [kiviFor, setKiviFor] = useState(null);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [dateF, setDateF] = useState(today());
  const [docF, setDocF] = useState("");
  const [deptF, setDeptF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [search, setSearch] = useState("");
  const [wizardOpen, setWizardOpen] = useState(sp.get("new") === "1");
  const [wizardPatientId, setWizardPatientId] = useState(sp.get("patient") || "");
  const [justCreated, setJustCreated] = useState(null);

  // Re-open wizard when shortcut navigates here with new params
  useEffect(() => {
    if (sp.get("new") === "1") setWizardOpen(true);
    const pid = sp.get("patient") || "";
    if (pid) setWizardPatientId(pid);
  }, [sp]);

  const load = () => {
    const p = {};
    if (dateF) p.date = dateF;
    if (docF) p.doctor_id = docF;
    if (deptF) p.department = deptF;
    if (statusF) p.status = statusF;
    api.get("/opd", { params: p }).then((r) => setRows(r.data));
    api.get("/opd-stats", { params: { date: dateF, doctor_id: docF, department: deptF } }).then((r) => setStats(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateF, docF, deptF, statusF]);
  useEffect(() => { api.get("/doctors").then((r) => setDoctors(r.data)); }, []);

  const filtered = search
    ? rows.filter((r) => (r.opd_no + r.uhid + r.patient_name + (r.doctor_name || "")).toLowerCase().includes(search.toLowerCase()))
    : rows;

  const depts = [...new Set(doctors.map((d) => d.department))];

  const cancel = async (o) => {
    if (!window.confirm(`Cancel OPD ${o.opd_no}?`)) return;
    await api.put(`/opd/${o.id}/status`, { status: "cancelled", reason: "Cancelled by user" });
    toast.success("OPD cancelled"); load();
  };
  const markComplete = async (o) => {
    await api.put(`/opd/${o.id}/status`, { status: "completed" });
    toast.success("Marked completed"); load();
  };
  const removeOPD = async (o) => {
    if (!window.confirm(`Delete OPD ${o.opd_no}?\nThis will remove all bill items, payments and prescriptions.  Cannot be undone.`)) return;
    try {
      await api.delete(`/opd/${o.id}`);
      toast.success("OPD deleted"); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const KPI = ({ icon: Ic, label, val, sub, tone = "blue", onClick, testid }) => (
    <button data-testid={testid} onClick={onClick} className="bg-white border p-4 text-left hover:border-blue-500 transition">
      <div className="flex justify-between">
        <div>
          <div className="label-tiny">{label}</div>
          <div className="font-display text-2xl font-semibold mt-2 data-mono">{val}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-9 h-9 flex items-center justify-center bg-${tone}-50 text-${tone}-700`}><Ic className="w-5 h-5" /></div>
      </div>
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">OPD Management</h1>
          <p className="text-sm text-slate-500">Live queue, filters and full patient-doctor OPD workflow for {new Date(dateF).toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long" })}.</p>
        </div>
        <Button data-testid="add-opd-btn" onClick={() => setWizardOpen(true)} className="bg-blue-700 hover:bg-blue-800 rounded-sm text-base h-10 px-6"><Plus className="w-5 h-5 mr-1" /> ADD OPD</Button>
      </div>

      {/* KPI cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI testid="opd-kpi-total" icon={ClipboardList} label="Today's OPD" val={stats.totals.total} onClick={() => setStatusF("")} />
          <KPI testid="opd-kpi-waiting" icon={Clock} label="Waiting" val={stats.totals.waiting} tone="amber" onClick={() => setStatusF("active")} />
          <KPI testid="opd-kpi-completed" icon={CheckCircle2} label="Completed" val={stats.totals.completed} tone="teal" onClick={() => setStatusF("completed")} />
          <KPI testid="opd-kpi-cancelled" icon={XCircle} label="Cancelled" val={stats.totals.cancelled} tone="red" onClick={() => setStatusF("cancelled")} />
          <KPI testid="opd-kpi-revenue" icon={ClipboardList} label="Consultation Revenue" val={fmtINR(stats.totals.revenue)} tone="blue" />
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white border p-3 flex gap-2 flex-wrap items-center">
        <Filter className="w-4 h-4 text-slate-500"/>
        <Input type="date" value={dateF} onChange={(e) => setDateF(e.target.value)} className="h-9 w-40" data-testid="opd-date"/>
        <select value={docF} onChange={(e) => setDocF(e.target.value)} className="h-9 border text-sm px-2" data-testid="opd-filter-doctor"><option value="">All Doctors</option>{doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select>
        <select value={deptF} onChange={(e) => setDeptF(e.target.value)} className="h-9 border text-sm px-2" data-testid="opd-filter-dept"><option value="">All Departments</option>{depts.map((d) => <option key={d}>{d}</option>)}</select>
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="h-9 border text-sm px-2"><option value="">All Status</option><option value="active">Waiting</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option><option value="converted">Converted</option></select>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400"/>
          <Input placeholder="Search OPD / UHID / patient" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-64 pl-8" data-testid="opd-search"/>
        </div>
        <div className="ml-auto text-xs text-slate-500">{filtered.length} of {rows.length}</div>
      </div>

      {/* Doctor-wise + Dept-wise summary */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border">
            <div className="p-3 border-b label-tiny">Doctor-wise OPD</div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Doctor</th><th className="label-tiny">Department</th><th className="label-tiny text-center">Count</th><th className="label-tiny text-right">Revenue</th></tr></thead>
              <tbody>
                {stats.by_doctor.map((r) => (
                  <tr key={r.doctor} className="border-b"><td className="p-2">{r.doctor}</td><td className="text-xs">{r.department}</td><td className="text-center font-mono">{r.count}</td><td className="text-right font-mono">{fmtINR(r.revenue)}</td></tr>
                ))}
                {stats.by_doctor.length === 0 && <tr><td colSpan="4" className="p-4 text-center text-sm text-slate-500">No OPDs on this day</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="bg-white border">
            <div className="p-3 border-b label-tiny">Department-wise OPD</div>
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left"><th className="label-tiny p-2">Department</th><th className="label-tiny text-center">Count</th></tr></thead>
              <tbody>
                {stats.by_department.map((r) => (
                  <tr key={r.department} className="border-b"><td className="p-2">{r.department}</td><td className="text-center font-mono">{r.count}</td></tr>
                ))}
                {stats.by_department.length === 0 && <tr><td colSpan="2" className="p-4 text-center text-sm text-slate-500">No OPDs on this day</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* OPD List */}
      <div className="bg-white border">
        <div className="p-3 border-b label-tiny">OPD Records</div>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left">
            <th className="label-tiny p-2">OPD No</th><th className="label-tiny">UHID</th><th className="label-tiny">Patient</th><th className="label-tiny">Doctor</th><th className="label-tiny">Department</th><th className="label-tiny">Time</th><th className="label-tiny">Status</th><th className="label-tiny w-72">Print / Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map((o) => {
              const status = o.status || "waiting";
              const pop = (url) => (e) => { e.stopPropagation(); window.open(url, "_blank"); };
              return (
                <tr key={o.id} className="border-b hover:bg-blue-50 cursor-pointer" onClick={() => nav(`/opd/${o.id}`)}>
                  <td className="p-2 font-mono text-xs">{o.opd_no}</td>
                  <td className="font-mono text-xs">{o.uhid}</td>
                  <td className="font-medium">{o.patient_name}</td>
                  <td>{o.doctor_name}</td>
                  <td className="text-xs">{o.department}</td>
                  <td className="text-xs">{fmtDT(o.created_at)}</td>
                  <td>
                    <Badge className={`rounded-sm ${status === "completed" ? "bg-teal-100 text-teal-800" : status === "cancelled" ? "bg-red-100 text-red-800" : status === "converted" ? "bg-purple-100 text-purple-800" : "bg-amber-100 text-amber-800"}`}>{status}</Badge>
                  </td>
                  <td>
                    <div className="flex gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
                      {/* Quick-print buttons (Bill A4 / 58mm / 80mm + Rx) */}
                      <button onClick={pop(`/bill/opd/${o.id}/print?layout=a4`)} className="text-[10px] px-2 py-1 border border-blue-300 text-blue-700 hover:bg-blue-50 rounded-sm font-semibold" title="Print A4 Bill" data-testid={`opd-print-a4-${o.opd_no}`}>A4</button>
                      <button onClick={pop(`/bill/opd/${o.id}/print?layout=thermal58`)} className="text-[10px] px-2 py-1 border border-blue-300 text-blue-700 hover:bg-blue-50 rounded-sm font-semibold" title="Print 58mm Receipt" data-testid={`opd-print-58-${o.opd_no}`}>58mm</button>
                      <button onClick={pop(`/bill/opd/${o.id}/print?layout=thermal80`)} className="text-[10px] px-2 py-1 border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-sm" title="Print 80mm Receipt" data-testid={`opd-print-80-${o.opd_no}`}>80</button>
                      <button onClick={pop(`/prescription/${o.id}/print`)} className="text-[10px] px-2 py-1 border border-teal-300 text-teal-700 hover:bg-teal-50 rounded-sm font-semibold" title="Print Rx" data-testid={`opd-print-rx-${o.opd_no}`}>Rx</button>
                      {status !== "completed" && status !== "cancelled" && status !== "converted" && (
                        <>
                          <button onClick={() => markComplete(o)} className="text-[10px] px-2 py-1 border border-teal-300 text-teal-700 hover:bg-teal-50 rounded-sm">Complete</button>
                          <button onClick={() => cancel(o)} className="text-[10px] px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm">Cancel</button>
                        </>
                      )}
                      {canDelete && (
                        <button onClick={() => removeOPD(o)} className="text-[10px] px-2 py-1 border border-red-400 text-red-700 hover:bg-red-100 rounded-sm inline-flex items-center gap-1" title="Delete OPD" data-testid={`opd-delete-${o.opd_no}`}><Trash2 className="w-3 h-3"/>Delete</button>
                      )}
                      {!o.abha_number && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setKiviFor({
                            id: o.patient_id, uhid: o.uhid, name: o.patient_name,
                            age: o.age, gender: o.gender, mobile: o.patient_mobile,
                          }); }}
                          className="text-[10px] px-2 py-1 border border-teal-400 text-teal-700 hover:bg-teal-50 rounded-sm inline-flex items-center gap-1"
                          title="Create ABHA via KiviHealth"
                          data-testid={`opd-kivi-${o.opd_no}`}
                        ><Shield className="w-3 h-3"/>KiviHealth</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-sm text-slate-500">No OPD records match current filters</td></tr>}
          </tbody>
        </table>
      </div>

      <OPDWizard open={wizardOpen} onClose={() => { setWizardOpen(false); setWizardPatientId(""); }} onCreated={(id, opdData) => { setWizardOpen(false); setWizardPatientId(""); load(); setJustCreated(opdData || { id }); }} doctors={doctors} presetPatientId={wizardPatientId} />
      <OPDPaymentDialog opd={justCreated} onClose={() => setJustCreated(null)} onDone={(id) => { setJustCreated(null); nav(`/opd/${id}`); }} />
      <KiviHealthBridge open={!!kiviFor} onOpenChange={(v) => { if (!v) setKiviFor(null); }} patient={kiviFor} onLinked={() => { setKiviFor(null); load(); }} />
    </div>
  );
}


function OPDPaymentDialog({ opd, onClose, onDone }) {
  const originalFee = opd?.consultation_fee || opd?.opd_charge || 0;
  const [charged, setCharged] = useState(0);
  const [received, setReceived] = useState(0);
  const [mode, setMode] = useState("Cash");
  const [status, setStatus] = useState("PAID");
  const [saved, setSaved] = useState(false);
  const [creditParty, setCreditParty] = useState({ name: "", contact: "", due_date: "" });
  useEffect(() => {
    if (opd) {
      setCharged(originalFee);
      setReceived(originalFee);
      setMode("Cash"); setStatus("PAID"); setSaved(false);
      setCreditParty({ name: "", contact: "", due_date: "" });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opd]);
  if (!opd) return null;

  // Rules
  const chargedNum = parseFloat(charged || 0);
  const receivedEffective = status === "PAID" ? chargedNum : Math.min(parseFloat(received || 0), chargedNum);
  const outstanding = Math.max(chargedNum - receivedEffective, 0);
  const needsCreditParty = status === "CREDIT" || (status === "PARTIAL" && outstanding > 0);

  const setStatusAndSync = (s) => {
    setStatus(s);
    if (s === "PAID") setReceived(chargedNum);
    if (s === "CREDIT") setReceived(0);
  };
  const setChargedAndSync = (v) => {
    setCharged(v);
    if (status === "PAID") setReceived(v);
  };

  const record = async () => {
    if (needsCreditParty && !creditParty.name) return toast.error("Credit party name is required for CREDIT / PARTIAL");
    try {
      await api.post(`/opd/${opd.id}/payment`, {
        amount: receivedEffective,
        charged_amount: chargedNum,
        payment_mode: status === "CREDIT" && receivedEffective === 0 ? "Credit" : mode,
        payment_status: status,
        credit_party_name: creditParty.name,
        credit_party_contact: creditParty.contact,
        credit_due_date: creditParty.due_date,
      });
      toast.success(`OPD ${opd.opd_no || ""} · ${status}`);
      setSaved(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={!!opd} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="opd-payment-dialog">
        <DialogHeader><DialogTitle>OPD Created — Confirm Payment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-sm">
            <div className="flex justify-between"><span className="text-slate-600">OPD No</span><span className="font-mono font-semibold">{opd.opd_no || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Patient</span><span className="font-medium">{opd.patient_name || ""}</span></div>
            <div className="flex justify-between text-xs"><span className="text-slate-500">Default Consultation Fee</span><span className="font-mono">₹{originalFee}</span></div>
          </div>
          <div><Label>Status</Label>
            <div className="grid grid-cols-4 gap-1">
              {["PAID","PARTIAL","CREDIT","PENDING"].map((s) => (
                <button key={s} type="button" onClick={() => setStatusAndSync(s)} data-testid={`opd-pay-status-${s}`}
                  className={`h-9 text-xs border rounded-sm ${status === s ? (s === "PAID" ? "border-teal-600 bg-teal-50 text-teal-800 font-semibold" : s === "CREDIT" ? "border-amber-600 bg-amber-50 text-amber-800 font-semibold" : "border-blue-600 bg-blue-50 text-blue-800 font-semibold") : "border-slate-300 text-slate-600"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount Charged</Label><Input type="number" value={charged} onChange={(e) => setChargedAndSync(e.target.value)} data-testid="opd-pay-charged"/></div>
            <div>
              <Label>Amount Received</Label>
              <Input type="number" value={status === "PAID" ? chargedNum : received} onChange={(e) => setReceived(e.target.value)} disabled={status === "PAID"} data-testid="opd-pay-received"/>
            </div>
          </div>
          <div><Label>Payment Mode</Label>
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full h-9 border px-2 text-sm" data-testid="opd-pay-mode">
              <option>Cash</option><option>UPI</option><option>Card</option>
            </select>
          </div>
          {needsCreditParty && (
            <div className="space-y-2 border-t pt-3">
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 p-2 rounded-sm">₹{outstanding} will be tracked as outstanding — enter debtor details for later collection.</div>
              <div><Label>Credit Party Name*</Label><Input value={creditParty.name} onChange={(e) => setCreditParty({ ...creditParty, name: e.target.value })} data-testid="opd-credit-name"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Contact / Mobile</Label><Input value={creditParty.contact} onChange={(e) => setCreditParty({ ...creditParty, contact: e.target.value })} data-testid="opd-credit-contact"/></div>
                <div><Label>Due Date</Label><Input type="date" value={creditParty.due_date} onChange={(e) => setCreditParty({ ...creditParty, due_date: e.target.value })} data-testid="opd-credit-due"/></div>
              </div>
            </div>
          )}
          <div className="border-t pt-2 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Charged</span><span className="font-mono">₹{chargedNum}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Received</span><span className="font-mono text-teal-800">₹{receivedEffective}</span></div>
            <div className="flex justify-between font-semibold"><span>Outstanding</span><span className={`font-mono ${outstanding > 0 ? "text-red-700" : "text-teal-700"}`}>₹{outstanding}</span></div>
          </div>
        </div>
        <DialogFooter>
          {!saved ? (
            <>
              <Button variant="outline" onClick={() => onDone(opd.id)}>Skip</Button>
              <Button onClick={record} data-testid="opd-pay-record" className="bg-blue-700 hover:bg-blue-800">Record</Button>
            </>
          ) : (
            <div className="flex gap-2 justify-end w-full">
              <Button variant="outline" onClick={() => window.open(`/bill/opd/${opd.id}/print?layout=thermal58`, "_blank")}>Receipt</Button>
              <Button variant="outline" onClick={() => window.open(`/bill/opd/${opd.id}/print?layout=a4`, "_blank")}>Full Bill</Button>
              <Button onClick={() => onDone(opd.id)} className="bg-teal-700 hover:bg-teal-800">Continue →</Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function OPDWizard({ open, onClose, onCreated, doctors, presetPatientId }) {
  const [step, setStep] = useState(0); // 0 = pick type, 1 = patient, 2 = doctor, 3 = confirm
  const [type, setType] = useState(""); // existing | new
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [pt, setPt] = useState(null);
  const [newPt, setNewPt] = useState(emptyPt);
  const [doc, setDoc] = useState(null);
  const [docSearch, setDocSearch] = useState("");
  const [docDept, setDocDept] = useState("");
  const [pmjay, setPmjay] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [scheme, setScheme] = useState("NON_PMJAY");
  const [pkg, setPkg] = useState(null);
  const [packages, setPackages] = useState([]);
  const [pkgQ, setPkgQ] = useState("");
  useEffect(() => { if (scheme === "PACKAGE") api.get("/pmjay-packages").then((r) => setPackages(r.data)); }, [scheme]);
  const [refDoctor, setRefDoctor] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep(0); setType(""); setPt(null); setNewPt(emptyPt); setDoc(null);
      setQ(""); setPmjay(false); setScheme("NON_PMJAY"); setPkg(null); setPkgQ("");
      setDupes([]);
    }
  }, [open]);

  // Preset patient from shortcut nav (Ctrl+N → search → select → routes here with ?patient=<id>).
  useEffect(() => {
    if (!open || !presetPatientId) return;
    (async () => {
      try {
        const { data } = await api.get(`/patients/${presetPatientId}`);
        setType("existing");
        setPt(data);
        setPmjay(data.pmjay || false);
        // Reset downstream state to prevent silently-inherited doctor/scheme from a prior wizard session
        setDoc(null);
        setDocSearch("");
        setDocDept("");
        setScheme("NON_PMJAY");
        setPkg(null);
        setPkgQ("");
        setRefDoctor("");
        setStep(2); // jump directly to doctor selection
        toast.success(`Continuing OPD for ${data.name}`);
      } catch (e) {
        // Preset patient couldn't be loaded — user can still search manually.
        console.warn("[OPD preset patient skipped]", e?.response?.status || e?.message);
      }
    })();
  }, [open, presetPatientId]);

  // Live duplicate-detection when filling the New Patient form
  useEffect(() => {
    if (type !== "new") { setDupes([]); return; }
    const name = (newPt.name || "").trim();
    const mobile = (newPt.mobile || "").replace(/\D/g, "");
    const query = mobile.length === 10 ? mobile : (name.length >= 3 ? name : "");
    if (!query) { setDupes([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients", { params: { q: query } });
        setDupes(data.slice(0, 8));
      } catch { setDupes([]); }
    }, 350);
    return () => clearTimeout(t);
  }, [type, newPt.name, newPt.mobile]);
  useEffect(() => {
    if (type !== "existing") return;
    if (!q || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/patients", { params: { q: q.trim() } });
      setResults(data);
      // Smart auto-select: full mobile (10 digits) or UHID (contains /) that yields exactly one match
      const cleaned = q.trim();
      const isFullMobile = /^\d{10}$/.test(cleaned);
      const isUhid = cleaned.includes("/");
      if ((isFullMobile || isUhid) && data.length === 1) {
        const p = data[0];
        setPt(p); setPmjay(p.pmjay || false); setStep(2);
        toast.success(`Auto-selected · ${p.name} (${p.uhid})`);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q, type]);

  const registerNew = async () => {
    if (!newPt.name || !newPt.mobile || !newPt.age) return toast.error("Name, age and mobile are required");
    if (dupes.length > 0) {
      const ok = window.confirm(`${dupes.length} existing patient(s) match this name/mobile. Register a NEW record anyway?\n\nClick Cancel to review the duplicates and reuse instead.`);
      if (!ok) return;
    }
    try {
      const { data } = await api.post("/patients", { ...newPt, age: parseInt(newPt.age) });
      toast.success(`Patient registered · ${data.uhid}`);
      setPt(data); setPmjay(data.pmjay); setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const createOPD = async () => {
    if (!pt || !doc) return toast.error("Patient and doctor required");
    if (scheme === "PACKAGE" && !pkg) return toast.error("Please select a package");
    setSaving(true);
    try {
      const payload = {
        patient_id: pt.id, doctor_id: doc.id,
        pmjay: scheme !== "NON_PMJAY",
        scheme,
        pmjay_package_code: pkg?.code || "",
        pmjay_package_name: pkg?.name || "",
        pmjay_package_amount: pkg?.amount || 0,
        reference_doctor: refDoctor,
      };
      const { data } = await api.post("/opd", payload);
      toast.success(`OPD ${data.opd_no} created`);
      onCreated(data.id, { ...data, patient_name: pt.name });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const depts = [...new Set(doctors.map((d) => d.department))];
  const filteredDocs = doctors.filter((d) =>
    d.status === "active" &&
    (!docSearch || d.name.toLowerCase().includes(docSearch.toLowerCase())) &&
    (!docDept || d.department === docDept)
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>New OPD · Step {step + 1} of 4</DialogTitle>
          <div className="flex gap-1 mt-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={`h-1 flex-1 ${i <= step ? "bg-blue-700" : "bg-slate-200"}`}></div>
            ))}
          </div>
        </DialogHeader>

        {/* Step 0: Patient Type */}
        {step === 0 && (
          <div>
            <div className="label-tiny mb-3">Select Patient Type</div>
            <div className="grid grid-cols-2 gap-4">
              <button data-testid="pt-existing" onClick={() => { setType("existing"); setStep(1); }} className="border p-6 rounded-sm hover:border-blue-500 hover:bg-blue-50 transition text-left">
                <User className="w-8 h-8 text-blue-700 mb-3" />
                <div className="font-display font-semibold text-lg">Existing Patient</div>
                <div className="text-xs text-slate-500 mt-1">Search by UHID / Name / Mobile / OPD number</div>
              </button>
              <button data-testid="pt-new" onClick={() => { setType("new"); setStep(1); }} className="border p-6 rounded-sm hover:border-blue-500 hover:bg-blue-50 transition text-left">
                <UserPlus className="w-8 h-8 text-teal-700 mb-3" />
                <div className="font-display font-semibold text-lg">New Patient</div>
                <div className="text-xs text-slate-500 mt-1">Register patient with auto-generated UHID</div>
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Patient Search or New Registration */}
        {step === 1 && type === "existing" && (
          <div>
            <div className="label-tiny mb-2">Smart Patient Search</div>
            <p className="text-[11px] text-slate-500 mb-2">Type UHID (e.g. <span className="font-mono">DH2405/000001</span>), 10-digit mobile or partial name — a unique match auto-selects.</p>
            <Input data-testid="wiz-search" placeholder="Search by UHID / Name / Mobile / OPD Number" value={q} onChange={(e) => setQ(e.target.value)} className="mb-3" autoFocus />
            <div className="max-h-72 overflow-y-auto border">
              {results.slice(0, 30).map((p) => (
                <div key={p.id} data-testid={`wiz-pt-${p.uhid}`} onClick={() => { setPt(p); setPmjay(p.pmjay || false); setStep(2); }} className={`p-2 border-b cursor-pointer hover:bg-blue-50 ${pt?.id === p.id ? "bg-blue-50" : ""}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium text-sm">{p.name}</div>
                      <div className="text-xs text-slate-500">{p.age}/{p.gender} · {p.mobile} · {p.blood_group || "N/A"}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-mono text-slate-500">{p.uhid}</div>
                      {p.pmjay && <Badge className="bg-teal-100 text-teal-800 rounded-sm text-[10px]">PMJAY</Badge>}
                    </div>
                  </div>
                </div>
              ))}
              {q && q.length >= 2 && results.length === 0 && <div className="p-3 text-xs text-slate-500">No match. Try different keyword or register as new patient.</div>}
            </div>
          </div>
        )}

        {step === 1 && type === "new" && (
          <div>
            <div className="label-tiny mb-2">New Patient Registration</div>
            {dupes.length > 0 && (
              <div className="mb-3 border-2 border-amber-400 bg-amber-50 rounded-sm p-3" data-testid="wiz-dupe-warning">
                <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm mb-2">
                  <AlertTriangle className="w-4 h-4"/> Possible duplicate — {dupes.length} existing patient{dupes.length > 1 ? "s" : ""} matched
                </div>
                <div className="text-[11px] text-amber-800 mb-2">Click any row to reuse the existing patient and skip re-registration. If truly a new person, ignore and continue below.</div>
                <div className="max-h-40 overflow-y-auto bg-white border">
                  {dupes.map((p) => (
                    <div key={p.id} data-testid={`wiz-dupe-${p.uhid}`}
                      onClick={() => { setPt(p); setPmjay(p.pmjay || false); setStep(2); toast.success(`Reused existing · ${p.name}`); }}
                      className="p-2 border-b cursor-pointer hover:bg-amber-100">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium text-sm">{p.name}</div>
                          <div className="text-xs text-slate-500">{p.age}/{p.gender} · {p.mobile} · {p.city || "-"}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs font-mono text-slate-500">{p.uhid}</div>
                          {p.pmjay && <Badge className="bg-teal-100 text-teal-800 rounded-sm text-[10px]">PMJAY</Badge>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              {[["name","Full Name*"],["age","Age*"],["dob","Date of Birth"],["mobile","Mobile*"],["alt_mobile","Alternate Mobile"],["relative_name","Relative Name"],["relative_mobile","Relative Mobile"],["city","City"],["pincode","Pincode"],["reference_doctor","Reference Doctor"]].map(([k, l]) => (
                <div key={k}>
                  <Label className="label-tiny">{l}</Label>
                  {k === "dob" ? (
                    <Input type="date" value={newPt[k]} onChange={(e) => setNewPt({ ...newPt, [k]: e.target.value })} className="h-9 mt-1" data-testid={`wiz-npt-${k}`} />
                  ) : (
                    <VoiceInput
                      as="input"
                      testid={`wiz-npt-${k}`}
                      className="mt-1"
                      numeric={NEWPT_NUMERIC[k] !== undefined}
                      maxLength={NEWPT_NUMERIC[k]}
                      value={newPt[k] || ""}
                      onChange={(v) => setNewPt({ ...newPt, [k]: v })}
                    />
                  )}
                </div>
              ))}
              <div>
                <Label className="label-tiny">Gender*</Label>
                <select className="h-9 border w-full mt-1 px-2 text-sm" value={newPt.gender} onChange={(e) => setNewPt({ ...newPt, gender: e.target.value })}><option>Male</option><option>Female</option><option>Other</option></select>
              </div>
              <div>
                <Label className="label-tiny">Blood Group</Label>
                <select className="h-9 border w-full mt-1 px-2 text-sm" value={newPt.blood_group} onChange={(e) => setNewPt({ ...newPt, blood_group: e.target.value })}><option value="">--</option>{["A+","A-","B+","B-","O+","O-","AB+","AB-"].map(b => <option key={b}>{b}</option>)}</select>
              </div>
              <div className="col-span-2">
                <Label className="label-tiny">Address</Label>
                <VoiceInput as="input" testid="wiz-npt-address" className="mt-1" value={newPt.address} onChange={(v) => setNewPt({ ...newPt, address: v })} />
              </div>
              <label className="flex items-center gap-2 self-end text-sm pb-2"><input type="checkbox" checked={newPt.pmjay} onChange={(e) => setNewPt({ ...newPt, pmjay: e.target.checked })} data-testid="wiz-npt-pmjay" /> PMJAY Beneficiary</label>
            </div>
            <Button data-testid="wiz-register-pt" onClick={registerNew} className="mt-3 bg-teal-700 hover:bg-teal-800 rounded-sm">Register & Continue →</Button>
          </div>
        )}

        {/* Step 2: Doctor Selection */}
        {step === 2 && (
          <div>
            <div className="bg-blue-50 border border-blue-200 p-3 mb-3 flex items-center justify-between">
              <div className="text-sm"><b>{pt?.name}</b> · <span className="font-mono text-xs">{pt?.uhid}</span> · {pt?.age}/{pt?.gender} · {pt?.mobile}</div>
              {pt?.pmjay && <Badge className="bg-teal-100 text-teal-800 rounded-sm">PMJAY</Badge>}
            </div>
            <div className="label-tiny mb-2">SELECT DOCTOR FOR OPD</div>
            <div className="flex gap-2 mb-3">
              <Input placeholder="Search doctor" value={docSearch} onChange={(e) => setDocSearch(e.target.value)} className="h-9 max-w-xs" data-testid="wiz-doc-search" />
              <select value={docDept} onChange={(e) => setDocDept(e.target.value)} className="h-9 border text-sm px-2"><option value="">All Departments</option>{depts.map((d) => <option key={d}>{d}</option>)}</select>
            </div>
            <div className="grid grid-cols-3 gap-3 max-h-96 overflow-y-auto">
              {filteredDocs.map((d) => (
                <div key={d.id} data-testid={`wiz-doc-${d.name.replace(/\s/g,'-')}`} onClick={() => { setDoc(d); setStep(3); }} className={`border p-3 cursor-pointer transition ${doc?.id === d.id ? "border-blue-600 bg-blue-50 ring-2 ring-blue-200" : "border-slate-200 hover:border-blue-500"}`}>
                  <img src={d.photo || "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=200"} className="w-full h-24 object-cover rounded-sm" alt="" />
                  <div className="mt-2">
                    <div className="font-display font-semibold text-sm">{d.name}</div>
                    <div className="text-xs text-slate-500">{d.department}</div>
                    <div className="text-[11px] text-slate-500 mt-1">{d.qualification}</div>
                    <div className="flex justify-between mt-2 text-xs">
                      <span className="font-mono text-blue-700">OPD ₹{d.opd_charge}</span>
                      <span className="text-slate-400">Comm {d.opd_commission}%</span>
                    </div>
                  </div>
                </div>
              ))}
              {filteredDocs.length === 0 && <div className="col-span-3 text-center text-sm text-slate-500 p-6">No doctors match filter</div>}
            </div>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <div className="space-y-3">
            <div className="bg-slate-50 border p-3 space-y-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="label-tiny">Patient</span><div className="font-medium">{pt?.name}</div></div>
                <div><span className="label-tiny">UHID</span><div className="font-mono">{pt?.uhid}</div></div>
                <div><span className="label-tiny">Doctor</span><div className="font-medium">{doc?.name}</div></div>
                <div><span className="label-tiny">Department</span><div>{doc?.department}</div></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-tiny">Reference Doctor (optional)</Label>
                <Input value={refDoctor} onChange={(e) => setRefDoctor(e.target.value)} className="h-9 mt-1" placeholder="e.g. Dr. ABC (Hospital XYZ)" />
              </div>
              <div>
                <Label className="label-tiny">Scheme</Label>
                <div className="flex gap-1 mt-1">
                  {[["NON_PMJAY", "Non-PMJAY"], ["PMJAY", "PM-JAY"], ["PACKAGE", "Package"]].map(([k, l]) => (
                    <button key={k} type="button" data-testid={`wiz-scheme-${k}`}
                      onClick={() => { setScheme(k); if (k !== "PACKAGE") setPkg(null); }}
                      className={`flex-1 h-9 border text-xs rounded-sm ${scheme === k ? "border-teal-600 bg-teal-50 text-teal-700 font-semibold" : "border-slate-300 text-slate-600"}`}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
            {scheme === "PACKAGE" && (
              <div className="border p-3 bg-teal-50/40 rounded-sm">
                <Label className="label-tiny">Search & select PMJAY Package</Label>
                <Input value={pkgQ} onChange={(e) => setPkgQ(e.target.value)} placeholder="Search by code, name or department" className="h-9 mt-1" data-testid="wiz-pkg-search" />
                {pkg && <div className="mt-2 text-sm bg-white border p-2 rounded-sm flex justify-between"><span><b>{pkg.code}</b> · {pkg.name} · <span className="text-slate-500 text-xs">{pkg.department}</span></span><span className="font-mono font-semibold text-teal-700">₹{pkg.amount}</span></div>}
                {!pkg && (
                  <div className="max-h-48 overflow-y-auto mt-2 border bg-white">
                    {packages.filter((p) => !pkgQ || (p.code + p.name + (p.department||"")).toLowerCase().includes(pkgQ.toLowerCase())).slice(0, 30).map((p) => (
                      <div key={p.id} data-testid={`wiz-pkg-${p.code}`} onClick={() => setPkg(p)} className="p-2 border-b cursor-pointer hover:bg-teal-50 text-sm flex justify-between">
                        <span><b className="font-mono">{p.code}</b> · {p.name} <span className="text-xs text-slate-500">· {p.department}</span></span>
                        <span className="font-mono">₹{p.amount}</span>
                      </div>
                    ))}
                    {packages.length === 0 && <div className="p-3 text-xs text-slate-500">No packages loaded</div>}
                  </div>
                )}
              </div>
            )}
            <div className="border-t pt-3">
              <div className="flex justify-between items-center">
                <div className="text-sm text-slate-500">Consultation Fee</div>
                <div className="font-mono text-xl font-bold text-blue-700">₹{doc?.opd_charge}</div>
              </div>
              {scheme === "PACKAGE" && pkg && <div className="flex justify-between items-center mt-1"><div className="text-sm text-slate-500">PMJAY Package · {pkg.name}</div><div className="font-mono text-lg font-bold text-teal-700">₹{pkg.amount}</div></div>}
              <div className="text-xs text-slate-500 mt-1">The consultation screen opens next for clinical notes, prescription, investigations and billing.</div>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          {step > 0 && <Button variant="outline" onClick={() => setStep(step - 1)}><ArrowLeft className="w-4 h-4 mr-1"/> Back</Button>}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {step === 3 && <Button data-testid="wiz-create-opd" onClick={createOPD} disabled={saving} className="bg-blue-700 hover:bg-blue-800">Create OPD & Continue <ArrowRight className="w-4 h-4 ml-1"/></Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
